import { makeStyles } from '@material-ui/core/styles'
import { ChangeEvent, FC } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CatalogApartment } from '../../services/catalog'
import { bedroomCount, formatEuro } from './format'

export interface Filters {
  area: string
  /** minimum bedrooms; '0' = studios only */
  bedrooms: string
  maxRent: string
  furnished: boolean
}

const MAX_RENTS = [800, 1000, 1200, 1500, 2000]

/** Filters live in the query string, so a filtered list can be shared and survives going back. */
export const useFilters = (): [Filters, (changes: Partial<Filters>) => void, () => void] => {
  const location = useLocation()
  const history = useHistory()
  const params = new URLSearchParams(location.search)
  const filters: Filters = {
    area: params.get('area') || '',
    bedrooms: params.get('bedrooms') || '',
    maxRent: params.get('maxRent') || '',
    furnished: params.get('furnished') === '1'
  }
  const update = (changes: Partial<Filters>) => {
    const next = { ...filters, ...changes }
    const query = new URLSearchParams()
    if (next.area) query.set('area', next.area)
    if (next.bedrooms) query.set('bedrooms', next.bedrooms)
    if (next.maxRent) query.set('maxRent', next.maxRent)
    if (next.furnished) query.set('furnished', '1')
    const search = query.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }
  const clear = () => history.replace({ pathname: location.pathname, search: '' })
  return [filters, update, clear]
}

export const hasFilters = (filters: Filters) =>
  !!(filters.area || filters.bedrooms || filters.maxRent || filters.furnished)

export const applyFilters = (apartments: CatalogApartment[], filters: Filters) =>
  apartments.filter((apartment) => {
    if (filters.area && apartment.municipality !== filters.area) return false
    if (filters.bedrooms) {
      const count = bedroomCount(apartment.rooms)
      if (count === undefined) return false
      if (filters.bedrooms === '0' ? count !== 0 : count < Number(filters.bedrooms)) return false
    }
    if (filters.maxRent && (apartment.price === undefined || apartment.price > Number(filters.maxRent))) return false
    if (filters.furnished && !apartment.isFurnished) return false
    return true
  })

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 12,
    padding: 16,
    marginBottom: 12,
    borderRadius: 12,
    background: '#f5f7f6',
    [theme.breakpoints.down('xs')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 10,
      padding: 12
    }
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 160,
    fontSize: 13,
    color: '#5b635e',
    [theme.breakpoints.down('xs')]: {
      minWidth: 0
    }
  },
  select: {
    height: 42,
    padding: '0 12px',
    border: '1px solid #d5d9d7',
    borderRadius: 8,
    background: '#fff',
    color: '#1f2421',
    fontFamily: 'inherit',
    fontSize: 15,
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`
    }
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 42,
    fontSize: 15,
    color: '#1f2421',
    cursor: 'pointer',
    '& input': {
      width: 18,
      height: 18,
      margin: 0,
      accentColor: theme.palette.primary.main,
      cursor: 'pointer'
    }
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '0 0 20px',
    fontSize: 15,
    color: '#5b635e'
  },
  clear: {
    border: 0,
    padding: 0,
    background: 'none',
    color: theme.palette.primary.main,
    fontFamily: 'CircularBold',
    fontSize: 15,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  }
}))

interface CatalogFiltersProps {
  apartments: CatalogApartment[]
  shown: number
  filters: Filters
  onChange: (changes: Partial<Filters>) => void
  onClear: () => void
}

const CatalogFilters: FC<CatalogFiltersProps> = ({ apartments, shown, filters, onChange, onClear }) => {
  const classes = useStyles()
  const areaCounts = new Map<string, number>()
  apartments.forEach(({ municipality }) => areaCounts.set(municipality, (areaCounts.get(municipality) || 0) + 1))
  const areas = Array.from(areaCounts.entries()).sort((a, b) => b[1] - a[1])
  const select = (key: 'area' | 'bedrooms' | 'maxRent') => (event: ChangeEvent<HTMLSelectElement>) =>
    onChange({ [key]: event.target.value })

  return (
    <>
      <div className={classes.bar}>
        <label className={classes.field}>
          Area
          <select className={classes.select} value={filters.area} onChange={select('area')}>
            <option value=''>All areas</option>
            {areas.map(([name, count]) => (
              <option key={name} value={name}>
                {name} ({count})
              </option>
            ))}
          </select>
        </label>
        <label className={classes.field}>
          Bedrooms
          <select className={classes.select} value={filters.bedrooms} onChange={select('bedrooms')}>
            <option value=''>Any</option>
            <option value='0'>Studio</option>
            <option value='1'>1+</option>
            <option value='2'>2+</option>
            <option value='3'>3+</option>
          </select>
        </label>
        <label className={classes.field}>
          Max rent
          <select className={classes.select} value={filters.maxRent} onChange={select('maxRent')}>
            <option value=''>Any</option>
            {MAX_RENTS.map((rent) => (
              <option key={rent} value={rent}>
                {formatEuro(rent)} / month
              </option>
            ))}
          </select>
        </label>
        <label className={classes.checkbox}>
          <input
            type='checkbox'
            checked={filters.furnished}
            onChange={(event) => onChange({ furnished: event.target.checked })}
          />
          Furnished only
        </label>
      </div>
      <div className={classes.summary} aria-live='polite'>
        <span>
          Showing {shown} of {apartments.length} {apartments.length === 1 ? 'home' : 'homes'}
        </span>
        {hasFilters(filters) && (
          <button type='button' className={classes.clear} onClick={onClear}>
            Clear filters
          </button>
        )}
      </div>
    </>
  )
}

export default CatalogFilters
